import React, { useEffect, useRef } from 'react';

const STATE_COPY = {
  idle: 'Enter your order details above to start verification.',
  verifying: 'Confirming your payment... please wait.',
  verified: 'Order confirmed \u2713 You can submit your request now.',
  no_slots: 'Order confirmed, but all prepaid reviews for this order are already used.',
  blocked: 'Order confirmed, but this order is not eligible for submissions. Contact support if this is unexpected.',
  failed: "We're still waiting for confirmation. Please refresh in a moment or contact support.",
};

const OV_WAVE = [
  0.0, 0.053, 0.052, 0.055, 0.051, 0.046, 0.043, 0.043, 0.058, 0.08, 0.098, 0.159, 0.224, 0.273,
  0.294, 0.325, 0.391, 0.449, 0.52, 0.578, 0.598, 0.58, 0.592, 0.676, 0.641, 0.612, 0.622, 0.664,
  0.661, 0.616, 0.603, 0.606, 0.592, 0.627, 0.713, 0.642, 0.534, 0.465, 0.383, 0.309, 0.251, 0.221,
  0.212, 0.205, 0.189, 0.172, 0.155, 0.139, 0.125, 0.114, 0.105, 0.098, 0.094, 0.09, 0.085, 0.081,
  0.078, 0.075, 0.072, 0.07, 0.067, 0.064, 0.062, 0.06, 0.058, 0.056, 0.055, 0.054, 0.053, 0.053,
  0.054, 0.055, 0.056, 0.057, 0.057, 0.056, 0.055, 0.054, 0.053, 0.052, 0.052, 0.051, 0.05, 0.049,
  0.048, 0.047, 0.046, 0.046, 0.046, 0.046, 0.047, 0.048, 0.05, 0.054, 0.058, 0.064, 0.07, 0.078,
  0.088, 0.099, 0.111, 0.123, 0.136, 0.15, 0.164, 0.177, 0.191, 0.205, 0.219, 0.233, 0.249, 0.267,
  0.29, 0.317, 0.349, 0.383, 0.413, 0.425, 0.413, 0.385, 0.359, 0.334, 0.311, 0.29, 0.272, 0.255,
  0.24, 0.227, 0.216, 0.204, 0.195, 0.186, 0.179, 0.174, 0.169, 0.166, 0.163, 0.161, 0.158, 0.155,
  0.153, 0.152, 0.151, 0.152, 0.154, 0.158, 0.162, 0.168, 0.176, 0.193, 0.225, 0.277, 0.348, 0.434,
  0.525, 0.61, 0.682, 0.734, 0.752, 0.734, 0.701, 0.657, 0.604, 0.532, 0.446, 0.352, 0.269, 0.217,
  0.206, 0.229, 0.257, 0.278, 0.299, 0.324, 0.355, 0.389, 0.422, 0.454, 0.485, 0.513, 0.539, 0.564,
  0.59, 0.616, 0.646, 0.679, 0.713, 0.745, 0.769, 0.78, 0.779, 0.776, 0.779, 0.786, 0.797, 0.813,
  0.835, 0.865, 0.899, 0.934, 0.966, 0.99, 1, 0.998, 0.99, 0.977, 0.96, 0.94, 0.917, 0.89, 0.859,
  0.824, 0.782, 0.734, 0.679, 0.622, 0.571, 0.527, 0.496, 0.479, 0.478, 0.481, 0.47, 0.434, 0.393,
  0.36, 0.345, 0.34, 0.344, 0.356, 0.374, 0.394, 0.414, 0.426, 0.425, 0.401, 0.369, 0.336, 0.304,
  0.273, 0.24, 0.211, 0.185, 0.161, 0.14, 0.122, 0.108, 0.099, 0.094, 0.094, 0.097, 0.105, 0.125,
  0.151, 0.169, 0.166, 0.124, 0.139, 0.047,
];

const MIN_BARS = 60;
const MAX_BARS = 260;
const MIN_BAR_HEIGHT = 2.6;

export default function OrderVerificationLoader({
  state = 'verifying',
  showCaption = true,
  colorMode = 'full',
  revealDurationMs = 20000,
}) {
  const waveRef = useRef(null);
  const baseTrackRef = useRef(null);
  const fillTrackRef = useRef(null);
  const showBar = state === 'verifying';
  const copy = STATE_COPY[state] || STATE_COPY.verifying;
  const captionClassName = state === 'verified'
    ? 'ov-caption ov-caption--ok'
    : 'ov-caption ov-caption--pending';

  useEffect(() => {
    if (!showBar) return undefined;
    const waveEl = waveRef.current;
    const baseTrack = baseTrackRef.current;
    const fillTrack = fillTrackRef.current;
    if (!waveEl || !baseTrack || !fillTrack) return undefined;
    const doc = waveEl.ownerDocument || document;
    const win = doc.defaultView || window;
    let cancelled = false;

    const buildBars = () => {
      if (cancelled || !waveEl || !baseTrack || !fillTrack) return;
      const width = waveEl.clientWidth || 720;
      const height = waveEl.clientHeight || 60;
      const styles = win.getComputedStyle ? win.getComputedStyle(waveEl) : null;
      const bw = styles ? parseFloat(styles.getPropertyValue('--ov-bw')) || 10 : 10;
      const gap = styles ? parseFloat(styles.getPropertyValue('--ov-gap')) || 3 : 3;
      const cell = Math.max(1, bw + gap);
      const columns = Math.max(MIN_BARS, Math.min(MAX_BARS, Math.floor(width / cell)));

      const seg1 = doc.createElement('div');
      seg1.className = 'ov-seg';
      for (let i = 0; i < columns; i += 1) {
        seg1.appendChild(makeBar(doc, i, columns, height));
      }

      const seg2 = doc.createElement('div');
      seg2.className = 'ov-seg';
      seg2.innerHTML = seg1.innerHTML;

      baseTrack.innerHTML = '';
      baseTrack.appendChild(seg1);
      baseTrack.appendChild(seg2);

      const shift = seg1.getBoundingClientRect().width + gap;
      waveEl.style.setProperty('--ov-shiftNeg', `${(-shift).toFixed(2)}px`);

      fillTrack.innerHTML = baseTrack.innerHTML;

      const now = (win.performance && win.performance.now) ? win.performance.now() : Date.now();
      const streamDurMs = parseCssTimeToMs(styles?.getPropertyValue('--ov-streamDur'), 2900);
      const pulseDurMs = 980;
      const streamPhase = -(now % streamDurMs);
      const pulsePhase = -(now % pulseDurMs);
      waveEl.style.setProperty('--ov-streamDelay', `${streamPhase}ms`);
      waveEl.style.setProperty('--ov-barDelay', `${pulsePhase}ms`);

      baseTrack.getBoundingClientRect();
      fillTrack.getBoundingClientRect();
    };

    buildBars();
    win.addEventListener('resize', buildBars);
    return () => {
      cancelled = true;
      win.removeEventListener('resize', buildBars);
    };
  }, [showBar]);

  return (
    <section className={`ov-wrap ov-state-${state}`} aria-live="polite">
      {showCaption && state !== 'verifying' && <div className={captionClassName}>{copy}</div>}
      {showBar && (
        <div
          className={`ov-wave${colorMode === 'full' ? ' ov-wave--full' : ''}`}
          aria-hidden="true"
          ref={waveRef}
          style={{ '--ov-fillDur': `${revealDurationMs}ms` }}
        >
          <div className="ov-layer ov-base">
            <div className="ov-track" ref={baseTrackRef} />
          </div>
          <div className="ov-layer ov-fill">
            <div className="ov-track" ref={fillTrackRef} />
          </div>
        </div>
      )}
    </section>
  );
}

function makeBar(doc, index, total, height) {
  const t = total > 1 ? index / (total - 1) : 0;
  const sample = sampleWave(t);
  const maxHeight = Math.max(MIN_BAR_HEIGHT, height);
  const barHeight = MIN_BAR_HEIGHT + sample * (maxHeight - MIN_BAR_HEIGHT);
  const opacity = 0.18 + sample * 0.82;
  const el = doc.createElement('div');
  el.className = 'ov-bar';
  el.style.setProperty('--ov-h', `${barHeight.toFixed(2)}px`);
  el.style.setProperty('--ov-op', opacity.toFixed(3));
  el.style.setProperty('--ov-boost', sample.toFixed(3));
  el.style.setProperty('--ov-i', String(index));
  return el;
}

function sampleWave(t) {
  const clamped = Math.min(1, Math.max(0, t));
  const scaled = clamped * (OV_WAVE.length - 1);
  const i = Math.floor(scaled);
  const frac = scaled - i;
  const a = OV_WAVE[i] ?? 0;
  const b = OV_WAVE[i + 1] ?? a;
  return a + (b - a) * frac;
}

function parseCssTimeToMs(value, fallback) {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  if (raw.endsWith('ms')) {
    const n = Number.parseFloat(raw.slice(0, -2));
    return Number.isFinite(n) ? n : fallback;
  }
  if (raw.endsWith('s')) {
    const n = Number.parseFloat(raw.slice(0, -1));
    return Number.isFinite(n) ? n * 1000 : fallback;
  }
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : fallback;
}
